import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { EmptyState } from "@routes-plan/ui";
import StandardModal from "../../../components/modales/StandardModal";
import axios from "axios";
import { useT } from "../../../i18n";
import { companyLocale, formatCurrency as companyMoney } from '../../../utils/format/currency';

interface ModalDeletedRecordDetailProps {
  open: boolean;
  onClose: () => void;
  recordType: "client" | "lend" | null;
  backupId: number | null;
  showAlert: (msg: string, severity?: "error" | "info" | "success" | "warning") => void;
}

const formatCurrency = (value: unknown) => companyMoney(Number(value || 0));

const formatDateTime = (value: unknown) => {
  if (!value) return "—";
  const d = new Date(String(value).replace(" ", "T"));
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleString(companyLocale());
};

const formatDate = (value: unknown) => {
  if (!value) return "—";
  const d = new Date(String(value).replace(" ", "T"));
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(companyLocale());
};

/** Dato del detalle: etiqueta pequeña y valor; el texto largo pasa de línea. */
const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={500} sx={{ overflowWrap: "anywhere" }}>
      {value === "" || value === null || value === undefined ? "—" : value}
    </Typography>
  </Box>
);

/** Sección del detalle con su título y los datos en rejilla. */
const Section: React.FC<{ title: React.ReactNode; children: React.ReactNode }> = ({ title, children }) => (
  <Box component="section" sx={{ minWidth: 0 }}>
    <Typography variant="subtitle2" component="h3" sx={{ mb: 1, pb: 0.5, borderBottom: 1, borderColor: "divider" }}>
      {title}
    </Typography>
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(150px, 100%), 1fr))", gap: 2 }}>
      {children}
    </Box>
  </Box>
);

const DeletedRecordDetailModal: React.FC<ModalDeletedRecordDetailProps> = ({
  open, onClose, recordType, backupId, showAlert,
}) => {
  const t = useT();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const reqIdRef = useRef(0);

  useEffect(() => {
    if (!open || !recordType || !backupId) {
      return;
    }

    let active = true;
    const requestId = ++reqIdRef.current;

    (async () => {
      setLoading(true);
      setData(null);
      try {
        const { data: resp } = await axios.get(`/reports/deleted-records/${recordType}/${backupId}`);
        if (!active || requestId !== reqIdRef.current) return;
        setData(resp?.data || null);
      } catch (error) {
        if (active) {
          console.error(error);
          showAlert(t("auditReports.detail.loadError"), "error");
        }
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => { active = false; };
  }, [open, recordType, backupId, showAlert, t]);

  const title = recordType === "client" ? t("auditReports.detail.clientTitle") : t("auditReports.detail.loanTitle");

  return (
    <StandardModal
      open={open}
      onClose={onClose}
      title={title}
      maxWidth={800}
      loading={loading}
      actions={
        <>
          <span />
          <Button variant="outlined" onClick={onClose}>
            {t("common.close")}
          </Button>
        </>
      }
    >
      {loading ? null : !data ? (
        <EmptyState dense title={t("auditReports.detail.noData")} />
      ) : (
        <Box sx={{ display: "grid", gap: 3, minWidth: 0 }}>
          <Section title={t("auditReports.detail.sectionAudit")}>
            <Field label={t("auditReports.deletedBy")} value={data.deletedByName || data.deletedByUsername} />
            <Field label={t("auditReports.dateTime")} value={formatDateTime(data.deletedAt)} />
            <Field label={t("auditReports.detail.office")} value={data.officeName} />
            <Field label={t("summaryReports.shared.route")} value={data.routeName} />
          </Section>
          {recordType === "client" ? (
            <>
              <Section title={t("auditReports.detail.sectionClient")}>
                <Field label={t("auditReports.originalId")} value={data.originalClientId} />
                <Field label={t("common.idNumber")} value={data.idNumber} />
                <Field label={t("common.name")} value={data.fullName} />
                <Field label={t("common.phone")} value={data.phone} />
                <Field label={t("common.cellphone")} value={data.mobilePhone} />
                <Field label={t("auditReports.detail.mainAddress")} value={data.mainAddress} />
                <Field label={t("auditReports.detail.secondAddress")} value={data.secondaryAddress} />
                <Field label={t("auditReports.detail.job")} value={data.job} />
                <Field label={t("auditReports.detail.creditLimit")} value={formatCurrency(data.creditLimit)} />
                <Field label={t("auditReports.detail.landmark")} value={data.landmark} />
              </Section>
              <Section title={t("auditReports.detail.sectionCoSigner")}>
                <Field label={t("common.name")} value={data.coSignerFullName} />
                <Field label={t("common.idNumber")} value={data.coSignerIdNumber} />
                <Field label={t("common.address")} value={data.coSignerAddress} />
                <Field label={t("common.phone")} value={data.coSignerPhone} />
              </Section>
            </>
          ) : (
            <>
              <Section title={t("auditReports.detail.sectionLoan")}>
                <Field label={t("auditReports.originalId")} value={data.originalLoanId} />
                <Field label={t("auditReports.client")} value={data.clientName} />
                <Field label={t("auditReports.detail.loanType")} value={data.loanTypeName} />
                <Field label={t("auditReports.detail.contract")} value={data.contract} />
                <Field label={t("auditReports.detail.location")} value={data.location} />
              </Section>
              <Section title={t("auditReports.detail.sectionValues")}>
                <Field label={t("auditReports.detail.loanAmount")} value={formatCurrency(data.value)} />
                <Field label={t("auditReports.detail.totalValue")} value={formatCurrency(data.totalValue)} />
                <Field label={t("auditReports.detail.balance")} value={formatCurrency(data.balance)} />
                <Field label={t("auditReports.detail.profit")} value={formatCurrency(data.interestRate)} />
                <Field label={t("auditReports.detail.insuranceCharge")} value={formatCurrency(data.insuranceCharge)} />
                <Field label={t("auditReports.detail.dailyInstallment")} value={formatCurrency(data.installmentValue)} />
                <Field label={t("auditReports.detail.termDays")} value={data.termDays} />
                <Field label={t("auditReports.detail.daysLate")} value={data.daysLate} />
                <Field label={t("auditReports.detail.paidTotal")} value={formatCurrency(data.paidValue)} />
              </Section>
              <Section title={t("auditReports.detail.sectionDates")}>
                <Field label={t("auditReports.detail.loanDate")} value={formatDate(data.date)} />
                <Field label={t("auditReports.detail.createdAt")} value={formatDateTime(data.createdAt)} />
                <Field label={t("auditReports.detail.startDate")} value={formatDate(data.startDate)} />
                <Field label={t("auditReports.detail.refinanceDate")} value={formatDateTime(data.refinanceDate)} />
                <Field label={t("auditReports.detail.statusDate")} value={formatDateTime(data.statusDate)} />
              </Section>
            </>
          )}
        </Box>
      )}
    </StandardModal>
  );
};

export default DeletedRecordDetailModal;
